/* eslint-disable no-await-in-loop */
import BaseComponent from 'scripts/BaseComponent.js';
import Cart from '../cart';

export default class MiniCartProgress extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      cartTotal: '.js-cartTotal',
      progressBar: '.js-progressBar',
      minSpendBlock: '.js-minSpendBlock',
      progressFill: '.js-progressFill',
      progressComplete: '.js-progressComplete',
      progressIncomplete: '.js-progressIncomplete',
      previousRewards: '.js-previousGifts',
      amountUntilNextMilestone: '.js-amountUntilNextMilestone',
      nextMilestoneReward: '.js-nextMilestoneReward',
    };

    this.dom = {
      $component: component,
      $cartTotal: document.querySelector(this.refs.cartTotal),
      $progressBar: this.$get(this.refs.progressBar),
      $minSpendBlocks: this.$getAll(this.refs.minSpendBlock),
      $progressFill: this.$get(this.refs.progressFill),
      $progressComplete: this.$get(this.refs.progressComplete),
      $progressIncomplete: this.$get(this.refs.progressIncomplete),
      $previousRewards: this.$get(this.refs.previousRewards),
      $amountUntilNextMilestone: this.$get(this.refs.amountUntilNextMilestone),
      $nextMilestoneReward: this.$get(this.refs.nextMilestoneReward),
    };

    this.state = {
      mountCartItemsTotals: 'mountcarttotals',
      progressEnabled: false,
      progressTotal: 0,
      currentMilestone: 0,
      milestoneSpendThresholds: [],
      milestoneRewards: [],
    };

    this.mount();
    this.init();
  }

  mount() {
    const { state } = this;
    super.mount();

    this.setOnCustomEvent(state.mountCartItemsTotals, this.updateProgressBar);
  }

  init() {
    const { dom, state } = this;
    let { minSpend, maxSpend } = dom.$component.dataset;
    state.progressEnabled = !dom.$component.classList.contains('hide');

    if (!state.progressEnabled) return;

    // set upper and lower bounds if theme settings are left blank.
    minSpend = !minSpend ? 0 : parseInt(minSpend, 10);
    maxSpend = !maxSpend || maxSpend === '-1' ? Number.MAX_SAFE_INTEGER : parseInt(maxSpend, 10);

    // construct milestone threshold + rewards state
    state.milestoneSpendThresholds.push(minSpend);
    state.milestoneRewards.push('');
    dom.$minSpendBlocks.forEach((block, i) => {
      const { stepMax, stepLabel } = block.dataset;
      state.milestoneSpendThresholds.push(parseInt(stepMax, 10));
      state.milestoneRewards.push(stepLabel);

      if (i === dom.$minSpendBlocks.length - 1) {
        state.progressTotal = parseInt(stepMax, 10);
      }
    });
    state.milestoneSpendThresholds.push(maxSpend);
    state.milestoneRewards.push('');
    this.updateProgressBar();
  }

  getPrompt(index) {
    const { dom } = this;
    const [minSpendPrompt] = Array.from(dom.$minSpendBlocks).filter((prompt) => prompt.getAttribute('data-step-index') === `${index}`);
    return minSpendPrompt;
  }

  updateProgressFill(cartTotal, step) {
    const { dom, state } = this;
    const numMilestones = dom.$minSpendBlocks.length;
    const milestonePortion = 100 / numMilestones;
    const currentMilestone = state.milestoneSpendThresholds[step];
    const lastMilestone = state.milestoneSpendThresholds[step - 1];
    let progress = 100;

    if (step > 0 && step < numMilestones + 1) {
      // massage the numbers
      progress = milestonePortion * (step - 1);
      progress += ((cartTotal - lastMilestone)
        / (currentMilestone - lastMilestone))
        * (milestonePortion);
      dom.$amountUntilNextMilestone.innerHTML = `$${currentMilestone - cartTotal}`;
      dom.$nextMilestoneReward.innerHTML = `${state.milestoneRewards[step]}`;
    }
    dom.$progressFill.style.width = `${progress}%`;
  }

  getProductInCart(id) {
    const cartItems = document.querySelectorAll('.CartItem');
    return Array.from(cartItems).filter((item) => item.getAttribute('data-id') === `${id}`);
  }

  async updateProgressBar() {
    const { state, dom } = this;
    const cartTotal = parseInt(dom.$cartTotal.innerHTML.replace(/[\r\n\s$,+]/gm, ''), 10);
    const giftsToAdd = [];
    const giftsToRemove = {};
    let currentStep = 0;

    if (!state.progressEnabled) {
      return;
    }

    state.milestoneSpendThresholds.forEach((blockMaxSpend) => {
      if (cartTotal >= blockMaxSpend) {
        currentStep += 1;
      }
    });

    this.updateProgressFill(cartTotal, currentStep);

    if (currentStep === 0 || currentStep === state.milestoneSpendThresholds.length) {
      dom.$progressBar.classList.add('hide');
    } else {
      dom.$progressBar.classList.remove('hide');
    }

    // if still in the current step ...
    if (currentStep === state.currentMilestone) {
      return;
    }

    // update previous rewards string
    const unlockedRewards = state.milestoneRewards.slice(1, currentStep);
    if (unlockedRewards.length) {
      const rewardString = unlockedRewards.join(' and ');
      dom.$previousRewards.innerHTML = `You've unlocked ${rewardString}!`;
      dom.$previousRewards.classList.add('active');
    } else {
      dom.$previousRewards.innerHTML = '';
      dom.$previousRewards.classList.remove('active');
    }

    // toggle prompt messaging
    if (currentStep === state.milestoneSpendThresholds.length - 1) {
      dom.$progressComplete.classList.add('active');
      dom.$progressIncomplete.classList.remove('active');
    } else {
      dom.$progressComplete.classList.remove('active');
      dom.$progressIncomplete.classList.add('active');
    }

    /* eslint-disable-next-line */
    for (const block of dom.$minSpendBlocks) {
      const { stepIndex, stepGift } = block.dataset;
      const index = parseInt(stepIndex, 10);

      // update label state for each step
      if (index < currentStep) {
        block.classList.add('complete');
      } else {
        block.classList.remove('complete');
      }

      // determine gifts to add or remove for each step
      if (stepGift) {
        const [giftItem] = this.getProductInCart(stepGift);
        if (index < currentStep && !giftItem) {
          giftsToAdd.push({ id: stepGift, quantity: 1 });
        } else if (index >= currentStep && giftItem) {
          const { key } = giftItem.dataset;
          giftsToRemove[key] = 0;
        }
      }
    }

    if (giftsToAdd.length > 0) {
      await Cart.addItems(giftsToAdd);
    }

    if (Object.keys(giftsToRemove).length > 0) {
      await Cart.updateItems(giftsToRemove);
    }

    state.currentMilestone = currentStep;
  }
}
